import { ArrowRight, Clock, GraduationCap } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  CATALOGUE,
  CATALOGUE_SECTION,
  COVERAGE_MEANING,
  ENROLLED_SECTION,
  ENROLMENTS,
  HOW_BUILT,
  LEARNER,
  LEARNING_TIME,
  MY_PATH,
  PATH_REQUIREMENTS,
  TRAINING_CHROME,
  TRAINING_COPY,
  type CourseRow,
  type Segment,
} from '@/demo/content/trainingCenter'
import { cn } from '@/lib/utils'

/** Prose in which some words are real destinations (never a dead phrase). */
function Prose({ segments, className }: { segments: readonly Segment[]; className?: string }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <span key={index}>{segment.text}</span>
        ) : (
          <Link
            key={index}
            to={segment.href}
            className={cn('font-medium underline underline-offset-2 hover:no-underline', className)}
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

function CourseTableRow({ course }: { course: CourseRow }) {
  const { cohort } = course

  return (
    <TableRow>
      <TableHead scope="row" className="h-auto max-w-[20rem] whitespace-normal py-2.5 align-top">
        <span className="block text-sm text-foreground">{course.title}</span>
        <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
          {CATALOGUE_SECTION.coversPrefix}{' '}
          <Link
            to={course.skill.href}
            title={CATALOGUE_SECTION.matrixLinkTitle}
            className="text-ink-soft underline underline-offset-2 hover:text-foreground"
          >
            {course.skill.name}
          </Link>{' '}
          <span aria-hidden>·</span> {COVERAGE_MEANING[course.skill.coverage]}
        </span>
      </TableHead>

      <TableCell className="py-2.5 align-top">
        <Badge variant="outline" className="font-normal">
          {course.track}
        </Badge>
      </TableCell>

      <TableCell className="max-w-[16rem] whitespace-normal py-2.5 align-top text-sm">
        <Link
          to={course.taughtWith.href}
          className="text-foreground underline underline-offset-2 hover:no-underline"
        >
          {course.taughtWith.label}
        </Link>
      </TableCell>

      <TableCell className="v-figure py-2.5 text-right align-top text-sm tabular-nums text-foreground">
        {course.hoursLabel}
      </TableCell>

      <TableCell className="whitespace-normal py-2.5 align-top text-sm">
        <Link
          to={course.badge.href}
          className="text-foreground underline underline-offset-2 hover:no-underline"
        >
          {course.badge.label}
        </Link>
      </TableCell>

      <TableCell className="whitespace-normal py-2.5 align-top">
        <span className="v-figure block text-sm tabular-nums text-foreground">
          {cohort.weekLabel}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {cohort.full ? (
            <StatusPill tone="neutral">{CATALOGUE_SECTION.fullTag}</StatusPill>
          ) : (
            <span className="text-xs text-muted-foreground">{cohort.seatsLabel}</span>
          )}
          {cohort.fromMatrixQueue && (
            <StatusPill tone="proposed">{CATALOGUE_SECTION.queuedTag}</StatusPill>
          )}
        </span>
      </TableCell>
    </TableRow>
  )
}

/**
 * 1.8 Training center — the Competence Center from the learner's side: the
 * path she is on, the courses running for her, and a catalogue that exists
 * only because the skills matrix asked for it. Every course names the tracked
 * skill it closes and the library material it is taught from, so the page can
 * be checked against the two surfaces it depends on.
 */
export default function TrainingCenterPage() {
  return (
    <AppShell
      moduleLabel={TRAINING_CHROME.moduleLabel}
      breadcrumb={TRAINING_CHROME.breadcrumb}
      contextPill={TRAINING_CHROME.contextPill}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{TRAINING_COPY.title}</h1>
      {/* PX-W6.2 (AC-8): the demo badge — this surface renders seed content and stays network-quiet. */}
      <ConceptBadge />
        <p className="text-sm text-muted-foreground">{TRAINING_COPY.lede}</p>
      </header>

      {/* ── 1. The path this person is on ─────────────────────────────────── */}
      <section
        aria-labelledby="my-path-label"
        className="mt-5 rounded-lg border border-border bg-card px-4 py-4"
      >
        <div id="my-path-label">
          <SectionLabel>{MY_PATH.label}</SectionLabel>
        </div>

        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-3">
          <span
            aria-hidden
            className="flex h-11 w-11 items-center justify-center rounded-full bg-people-bg font-serif text-lg font-semibold text-people-ink"
          >
            {LEARNER.initial}
          </span>
          <div className="min-w-0">
            <h2 className="font-serif text-lg font-semibold">
              <Link to={LEARNER.href} className="underline underline-offset-2 hover:no-underline">
                {LEARNER.name}
              </Link>
            </h2>
            <p className="text-xs text-muted-foreground">{LEARNER.meta}</p>
          </div>
          <p className="flex flex-wrap items-center gap-2 text-sm sm:ml-4">
            <span className="text-ink-soft">{MY_PATH.fromRole}</span>
            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            <span className="font-medium text-foreground">{MY_PATH.toRole}</span>
            <Link
              to={MY_PATH.badge.href}
              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              {MY_PATH.badge.label}
            </Link>
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="v-figure font-serif text-[26px] font-semibold leading-8 tabular-nums text-foreground">
            {MY_PATH.progressLabel}
          </span>
          <div className="min-w-[16rem] flex-1">
            <Progress
              value={MY_PATH.progressPct}
              aria-label={`${MY_PATH.toRole} — ${MY_PATH.progressReading}`}
              className="bg-hairline"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">{MY_PATH.progressReading}</p>
          </div>
        </div>

        <h3 className="sr-only">{MY_PATH.requirementsLabel}</h3>
        <ul className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {PATH_REQUIREMENTS.map((requirement) => (
            <li key={requirement.key} className="rounded-md border border-border px-3 py-2">
              <span
                className={cn(
                  'block text-[13px]',
                  requirement.started ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {requirement.label}
              </span>
              <span className="v-figure mt-0.5 block text-xs tabular-nums text-muted-foreground">
                {requirement.reading}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground">
          <span className="font-medium">{MY_PATH.milestoneLabel}: </span>
          <Prose segments={MY_PATH.milestone} className="text-accent-foreground" />
        </p>
      </section>

      {/* ── 2. What is running for her today ──────────────────────────────── */}
      <section
        aria-labelledby="enrolled-label"
        className="mt-4 rounded-lg border border-border bg-card px-4 py-4"
      >
        <div id="enrolled-label">
          <SectionLabel>{ENROLLED_SECTION.label}</SectionLabel>
        </div>
        <p className="sr-only">{ENROLLED_SECTION.caption}</p>

        <ul className="mt-3">
          {ENROLMENTS.map((enrolment) => (
            <li
              key={enrolment.key}
              className="v-row flex flex-wrap items-start gap-x-6 gap-y-2 py-3 first:pt-0"
            >
              <div className="min-w-[14rem] flex-1">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                  {enrolment.title}
                  {enrolment.onPath && (
                    <StatusPill tone="people">{ENROLLED_SECTION.onPathTag}</StatusPill>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {ENROLLED_SECTION.taughtWithPrefix}{' '}
                  <Link
                    to={enrolment.taughtWith.href}
                    className="text-ink-soft underline underline-offset-2 hover:text-foreground"
                  >
                    {enrolment.taughtWith.label}
                  </Link>
                </p>
              </div>

              <div className="w-44">
                <p className="v-figure text-xs tabular-nums text-muted-foreground">
                  {enrolment.reading}
                </p>
                <Progress
                  value={enrolment.progressPct}
                  aria-hidden
                  className="mt-1.5 h-1.5 bg-hairline"
                />
              </div>

              <p className="text-sm">
                <span className="text-xs text-muted-foreground">
                  {ENROLLED_SECTION.awardsPrefix}{' '}
                </span>
                <Link
                  to={enrolment.badge.href}
                  className="text-foreground underline underline-offset-2 hover:no-underline"
                >
                  {enrolment.badge.label}
                </Link>
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* ── 3. Why the courses look like this ─────────────────────────────── */}
      <section aria-labelledby="how-built-label" className="mt-5">
        <div id="how-built-label" className="flex items-center gap-1.5">
          <GraduationCap className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <SectionLabel>{HOW_BUILT.label}</SectionLabel>
        </div>
        <div className="mt-2 grid gap-3 md:grid-cols-3">
          {HOW_BUILT.stats.map((stat) => (
            <KpiCard
              key={stat.key}
              label={stat.label}
              figure={stat.figure}
              qualifier={<Prose segments={stat.qualifier} className="text-ink-soft" />}
            />
          ))}
        </div>
      </section>

      {/* ── 4. What the learning costs the week ───────────────────────────── */}
      <section
        aria-labelledby="learning-time-label"
        className="mt-4 rounded-lg border border-border bg-card px-4 py-3"
      >
        <div id="learning-time-label" className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <SectionLabel>{LEARNING_TIME.label}</SectionLabel>
        </div>
        <p className="mt-1.5 flex flex-wrap items-baseline gap-x-2 text-sm leading-relaxed text-foreground">
          <span className="v-figure font-serif text-lg font-semibold tabular-nums">
            {LEARNING_TIME.figure}
          </span>
          <span className="text-muted-foreground">{LEARNING_TIME.figureQualifier}</span>
          <span className="basis-full sm:basis-auto">
            <Prose segments={LEARNING_TIME.prose} className="text-accent-foreground" />
          </span>
        </p>
      </section>

      {/* ── 5. Everything open to her ─────────────────────────────────────── */}
      <section aria-labelledby="catalogue-label" className="mt-5">
        <div id="catalogue-label">
          <SectionLabel>{CATALOGUE_SECTION.label}</SectionLabel>
        </div>
        <div className="mt-2 rounded-lg border border-border bg-card">
          <Table>
            <TableCaption className="sr-only">{CATALOGUE_SECTION.caption}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'pl-3')}>
                  {CATALOGUE_SECTION.columns.course}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {CATALOGUE_SECTION.columns.track}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {CATALOGUE_SECTION.columns.taughtWith}
                </TableHead>
                <TableHead scope="col" className={cn(HEAD_CLASS, 'text-right')}>
                  {CATALOGUE_SECTION.columns.hours}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {CATALOGUE_SECTION.columns.badge}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {CATALOGUE_SECTION.columns.cohort}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {CATALOGUE.map((course) => (
                <CourseTableRow key={course.key} course={course} />
              ))}
            </TableBody>
          </Table>
        </div>
      </section>

      <DensityNote>
        <Prose segments={TRAINING_COPY.densityNote} />
      </DensityNote>
    </AppShell>
  )
}
